import API from '@enums/api'
import {
    AssignOrgXeroContactsRes,
    OrgXeroContactRow,
    XeroContactOption,
    XeroRegion,
} from '@types'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'

/**
 * An org row with its contact names resolved onto it. The API only sends ids, but
 * the grid needs the names as real field values so sorting and the search box
 * work against them.
 */
export type OrgXeroContactGridRow = OrgXeroContactRow & {
    currentXeroContactName: string
    suggestedXeroContactName: string
}

export type ViewFilter = 'all' | 'unlinked' | 'suggested'

/**
 * Falls back to the raw id when a linked contact isn't in the contact list -
 * either it was deleted in Xero or its tenant failed to authenticate.
 */
const resolveNames = (
    orgs: OrgXeroContactRow[],
    contacts: XeroContactOption[]
): OrgXeroContactGridRow[] => {
    const nameById = new Map(
        contacts.map((contact) => [contact.id, contact.name])
    )

    return orgs.map((org) => ({
        ...org,
        currentXeroContactName: org.currentXeroContactId
            ? (nameById.get(org.currentXeroContactId) ??
              org.currentXeroContactId)
            : '',
        suggestedXeroContactName: org.suggestedXeroContactId
            ? (nameById.get(org.suggestedXeroContactId) ??
              org.suggestedXeroContactId)
            : '',
    }))
}

export default function useOrgXeroContactsPage() {
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState<string>()

    const [contacts, setContacts] = useState<XeroContactOption[]>([])
    const [rows, setRows] = useState<OrgXeroContactGridRow[]>([])

    /**
     * Unsaved org id -> contact id changes. Only entries that differ from the
     * org's saved link live here, so its size is the number of pending changes.
     */
    const [pending, setPending] = useState<Record<string, string>>({})
    const [saveResult, setSaveResult] = useState<AssignOrgXeroContactsRes>()

    const [view, setView] = useState<ViewFilter>('unlinked')
    const [searchText, setSearchText] = useState('')

    const contactsByRegion = useMemo(() => {
        const byRegion: Record<XeroRegion, XeroContactOption[]> = {
            USA: [],
            CAN: [],
        }

        contacts.forEach((contact) => byRegion[contact.region].push(contact))

        Object.values(byRegion).forEach((regionContacts) =>
            regionContacts.sort((a, b) => a.name.localeCompare(b.name))
        )

        return byRegion
    }, [contacts])

    const contactById = useMemo(
        () => new Map(contacts.map((contact) => [contact.id, contact])),
        [contacts]
    )

    /**
     * The one call that hits Xero. Deliberately only ever run from a click, never
     * on mount, to keep our API usage down.
     */
    const loadOptions = useCallback(async () => {
        setLoading(true)
        setError(undefined)

        const res = await API.ha.accounting.getOrgXeroContacts()

        if (!res.ok) {
            console.error(res)
            setError(
                res.message ??
                    'Failed to load Xero contacts. Check the Xero connection on the Send Invoice page and try again.'
            )
            setLoading(false)
            return
        }

        setContacts(res.value.contacts)
        setRows(resolveNames(res.value.orgs, res.value.contacts))
        setPending({})
        setSaveResult(undefined)
        setLoaded(true)
        setLoading(false)
    }, [])

    const setAssignment = useCallback(
        (row: OrgXeroContactGridRow, contactId: string | null) => {
            setPending((current) => {
                const next = { ...current }

                // Selecting nothing, or the saved link, isn't a change
                if (!contactId || contactId === row.currentXeroContactId) {
                    delete next[row.orgId]
                    return next
                }

                next[row.orgId] = contactId
                return next
            })
        },
        []
    )

    /**
     * Queues every suggestion that isn't already the saved link. Only touches the
     * rows currently in view so a filtered or searched list stays predictable.
     */
    const acceptSuggestions = useCallback(
        (visibleRows: OrgXeroContactGridRow[]) => {
            setPending((current) => {
                const next = { ...current }

                visibleRows.forEach((row) => {
                    if (
                        !row.region ||
                        !row.suggestedXeroContactId ||
                        row.suggestedXeroContactId === row.currentXeroContactId
                    ) {
                        return
                    }

                    next[row.orgId] = row.suggestedXeroContactId
                })

                return next
            })
        },
        []
    )

    const clearPending = useCallback(() => setPending({}), [])

    /**
     * Saves the queued changes and folds the applied ones into the rows in place.
     * Failures stay pending so they can be retried without another Xero fetch.
     */
    const save = useCallback(async () => {
        const assignments = Object.entries(pending).map(
            ([orgId, xeroContactId]) => ({ orgId, xeroContactId })
        )

        if (assignments.length === 0) return

        setSaving(true)
        setSaveResult(undefined)

        const res = await API.ha.accounting.assignOrgXeroContacts(assignments)

        if (!res.ok) {
            console.error(res)
            setError(res.message ?? 'Failed to save contact assignments')
            setSaving(false)
            return
        }

        const applied = new Set(res.value.applied)

        setRows((current) =>
            current.map((row) => {
                if (!applied.has(row.orgId)) return row

                const contact = contactById.get(pending[row.orgId])

                return {
                    ...row,
                    currentXeroContactId: pending[row.orgId],
                    currentXeroContactUrl: contact?.url ?? null,
                    currentXeroContactName: contact?.name ?? pending[row.orgId],
                }
            })
        )

        setPending((current) =>
            Object.fromEntries(
                Object.entries(current).filter(([orgId]) => !applied.has(orgId))
            )
        )

        if (applied.size > 0) {
            enqueueSnackbar(
                `Linked ${applied.size} organisation${applied.size === 1 ? '' : 's'} to Xero`,
                { variant: 'success' }
            )
        }

        setSaveResult(res.value)
        setSaving(false)
    }, [pending, contactById])

    const visibleRows = useMemo(() => {
        const search = searchText.trim().toLowerCase()

        return rows.filter((row) => {
            if (
                view === 'unlinked' &&
                (!row.region || row.currentXeroContactId)
            )
                return false

            if (
                view === 'suggested' &&
                (!row.suggestedXeroContactId ||
                    row.suggestedXeroContactId === row.currentXeroContactId)
            ) {
                return false
            }

            if (!search) return true

            return [
                row.orgName,
                row.orgId,
                row.region ?? '',
                row.currentXeroContactName,
                row.suggestedXeroContactName,
            ].some((field) => field.toLowerCase().includes(search))
        })
    }, [rows, view, searchText])

    return {
        loading,
        saving,
        loaded,
        error,
        rows,
        visibleRows,
        contactsByRegion,
        contactById,
        pending,
        pendingCount: Object.keys(pending).length,
        saveResult,
        view,
        setView,
        searchText,
        setSearchText,
        loadOptions,
        setAssignment,
        acceptSuggestions,
        clearPending,
        save,
    }
}
